import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Text, 
  Flex, 
  Input, 
  Select, 
  Button, 
  Divider, 
  useBreakpointValue, 
  Spinner,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  PinInput,
  PinInputField,
  HStack,
  VStack,
  Alert,
  AlertIcon,
  useDisclosure
} from '@chakra-ui/react';
import { getInternships, submitCertificateRequest, verifyOtpAndGetCertificate, resendCertificateOtp } from '../../../services/internshipService';

const InternshipHero = () => {
    const formWidth = useBreakpointValue({ base: '100%', md: '900px' });
    const isMobile = useBreakpointValue({ base: true, md: false });
    const cardHeight = useBreakpointValue({ base: 'auto', md: '112px' });
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();

    // State management
    const [internships, setInternships] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [otpSubmitting, setOtpSubmitting] = useState(false);
    const [pendingRequest, setPendingRequest] = useState(null);
    const [otp, setOtp] = useState('');
    const [otpError, setOtpError] = useState('');
    const [otpTimer, setOtpTimer] = useState(300); // 5 minutes = 300 seconds
    const [timerActive, setTimerActive] = useState(false);
    const [formData, setFormData] = useState({
        selectedInternship: '',
        mobileNumber: ''
    });

    // Fetch internships on component mount
    useEffect(() => {
        const fetchInternships = async () => {
            try {
                setLoading(true);
                const response = await getInternships();
                if (response.success) {
                    setInternships(response.internships);
                } else {
                    toast({
                        title: 'Error',
                        description: 'Failed to load internships. Please try again.',
                        status: 'error',
                        duration: 3000,
                        isClosable: true,
                    });
                }
            } catch (error) {
                console.error('Error loading internships:', error);
                toast({
                    title: 'Error',
                    description: 'Failed to load internships. Please try again.',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
            } finally {
                setLoading(false);
            }
        };

        fetchInternships();
    }, [toast]);

    // Timer effect for OTP expiration
    useEffect(() => {
        let interval;
        if (timerActive && otpTimer > 0) {
            interval = setInterval(() => {
                setOtpTimer((prev) => {
                    if (prev <= 1) {
                        setTimerActive(false);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [timerActive, otpTimer]);

    // Format timer display (mm:ss)
    const formatTimer = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Start timer function
    const startOtpTimer = () => {
        setOtpTimer(300); // Reset to 5 minutes
        setTimerActive(true);
    };

    // Handle form input changes
    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Handle form submission - initiates OTP process
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.selectedInternship || !formData.mobileNumber) {
            toast({
                title: 'Validation Error',
                description: 'Please fill in all required fields',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        // Validate mobile number format
        const mobileRegex = /^01[3-9]\d{8}$/;
        if (!mobileRegex.test(formData.mobileNumber)) {
            toast({
                title: 'Invalid Mobile Number',
                description: 'Please enter a valid Bangladeshi mobile number (e.g., 01712345678)',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        try {
                      setSubmitting(true);
            const selectedInternshipData = internships.find(i => i.id === formData.selectedInternship);
            
            // Submit initial certificate request - this will trigger OTP sending
            const response = await submitCertificateRequest({
                mobile: formData.mobileNumber,
                program_id: formData.selectedInternship
            });

                      
            if (response.success && response.requiresOtp) {
                // Store the pending request data and open OTP modal
                setPendingRequest({
                    mobile: formData.mobileNumber,
                    program_id: formData.selectedInternship,
                    internship_title: selectedInternshipData?.title || ''
                });
                
                setOtp('');
                setOtpError('');
                onOpen(); // Open OTP modal
                startOtpTimer(); // Start 5-minute timer
                
                toast({
                    title: 'OTP Sent!',
                    description: response.message || 'OTP has been sent to your mobile number',
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
            } else {
                toast({
                    title: 'Error',
                    description: response.message || 'You are not eligible for this certificate or request failed',
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error('Error submitting certificate request:', error);
            toast({
                title: 'Error',
                description: 'Failed to send OTP. Please try again.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setSubmitting(false);
        }
    };

    // Handle OTP submission and certificate generation
    const handleOtpSubmit = async () => {
        if (!otp || otp.length !== 4) {
            setOtpError('Please enter a valid 4-digit OTP');
            return;
        }

        try {
            setOtpSubmitting(true);
            setOtpError('');

            const verificationData = {
                mobile: pendingRequest.mobile,
                program_id: pendingRequest.program_id,
                otp: otp
            };

            const response = await verifyOtpAndGetCertificate(verificationData);
            
            if (response.success && response.certificateUrl) {
                onClose(); // Close OTP modal
                
                toast({
                    title: 'Certificate Generated!',
                    description: response.message || 'Your internship certificate has been generated successfully!',
                    status: 'success',
                    duration: 5000,
                    isClosable: true,
                });
                
                // Open certificate in new tab
                window.open(response.certificateUrl, '_blank');
                
                // Reset form
                setFormData({
                    selectedInternship: '',
                    mobileNumber: ''
                });
                setPendingRequest(null);
                setOtp('');
            } else {
                setOtpError(response.message || 'Invalid OTP. Please try again.');
            }
        } catch (error) {
            console.error('Error verifying OTP:', error);
            setOtpError('Failed to verify OTP. Please try again.');
        } finally {
            setOtpSubmitting(false);
        }
    };

    // Handle OTP resend
    const handleResendOtp = async () => {
        if (!pendingRequest) return;

        try {
            setOtpSubmitting(true);
            setOtpError('');

            const resendData = {
                mobile: pendingRequest.mobile,
                program_id: pendingRequest.program_id
            };

            const response = await resendCertificateOtp(resendData);
            
            if (response.success) {
                toast({
                    title: 'OTP Sent!',
                    description: response.message || 'A new OTP has been sent to your mobile number',
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
                setOtp(''); // Clear current OTP
                startOtpTimer(); // Restart timer on resend
            } else {
                setOtpError(response.message || 'Failed to resend OTP. Please try again.');
            }
        } catch (error) {
            console.error('Error resending OTP:', error);
            setOtpError('Failed to resend OTP. Please try again.');
        } finally {
            setOtpSubmitting(false);
        }
    };

    // Handle modal close
    const handleModalClose = () => {
        setOtp('');
        setOtpError('');
        setPendingRequest(null);
        setTimerActive(false);
        setOtpTimer(300);
        onClose();
    };
    return (
        <Box
            w="100%"
            minH="60vh"
            bgGradient="linear(to-b, #25575F, #1B3A4B)"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            pt={{ base: 8, md: 16 }}
            pb={{ base: 8, md: 16 }}
            position="relative"
            bgImage="url('/png/career-header.png')"
            bgSize= "cover"
            bgPosition="center"
            bgRepeat="no-repeat"
        >
            <Text fontWeight="bold" fontSize={{ base: '3xl', md: '5xl' }} color="#D6FF3F" mb={2} textAlign="center">
                Internship
            </Text>
            <Text fontSize={{ base: 'lg', md: '2xl' }} color="white" mb={8} textAlign="center">
                Download Your Certificate Here
            </Text>
            <Flex
                as="form"
                onSubmit={handleSubmit}
                w="100%"
                maxW="1300px"
                bg="white"
                borderRadius="16px"
                boxShadow="md"
                align="stretch"
                justify="space-between"
                direction={{ base: 'column', md: 'row' }}
                p={0}
                overflow="hidden"
            >
                <Flex flex={1} align="start" p={{ base: 4, md: 4 }} direction="column" justify="start" w="100%">
                    <Text mb={1} fontWeight="medium" fontSize={{ base: 'sm', md: 'md' }} w="100%" textAlign="left">
                        Select Your Internship Program
                    </Text>
                    <Select
                        placeholder={loading ? "Loading..." : "Select"}
                        fontSize={{ base: 'sm', md: 'md' }}
                        border="none"
                        borderBottom="1px solid #E5E7EB"
                        borderRadius={0}
                        _focus={{ borderColor: '#D6FF3F' }}
                        w="100%"
                        h="40px"
                        mb={{ base: 4, md: 0 }}
                        value={formData.selectedInternship}
                        onChange={(e) => handleInputChange('selectedInternship', e.target.value)}
                        disabled={loading}
                        isRequired
                    >
                        {loading ? (
                            <option disabled>Loading internships...</option>
                        ) : (
                            internships.map((internship) => (
                                <option key={internship.id} value={internship.id}>
                                    {internship.title}
                                </option>
                            ))
                        )}
                    </Select>
                    {loading && (
                        <Flex align="center" mt={1}>
                            <Spinner size="xs" mr={2} />
                            <Text fontSize="xs" color="gray.500">Loading internships...</Text>
                        </Flex>
                    )}
                </Flex>

                {/* Show divider only on desktop */}
                {!isMobile ? <Divider orientation="vertical" h="60px" borderColor="#E5E7EB" /> : null}

                <Flex flex={1} align="start" p={{ base: 4, md: 4 }} direction="column" justify="start" w="100%">
                    <Text mb={1} fontWeight="medium" fontSize={{ base: 'sm', md: 'md' }} w="100%" textAlign="left">
                        Mobile Number
                    </Text>
                    <Input
                        placeholder="Type"
                        fontSize={{ base: 'sm', md: 'md' }}
                        border="none"
                        borderBottom="1px solid #E5E7EB"
                        borderRadius={0}
                        _focus={{ borderColor: '#D6FF3F' }}
                        w="100%"
                        h="40px"
                        type="tel"
                        mb={{ base: 4, md: 0 }}
                        value={formData.mobileNumber}
                        onChange={(e) => handleInputChange('mobileNumber', e.target.value)}
                        isRequired
                    />
                </Flex>

                <Button
                    type="submit"
                    bg="#D6FF3F"
                    color="#222"
                    fontWeight="bold"
                    fontSize={{ base: 'md', md: 'lg' }}
                    borderRadius={{ base: '0 0 16px 16px', md: '0 16px 16px 0' }}
                    w={{ base: '100%', md: '240px' }}
                    h="auto"
                    _hover={{ bg: '#C0F000' }}
                    mt={{ base: 2, md: 0 }}
                    isLoading={submitting}
                    loadingText="SUBMITTING..."
                    isDisabled={loading || submitting}
                >
                    SUBMIT
                </Button>
            </Flex>

            {/* OTP Verification Modal */}
            <Modal isOpen={isOpen} onClose={handleModalClose} closeOnOverlayClick={false}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        <Text fontSize="lg" fontWeight="bold" color="gray.800">
                            Verify OTP
                        </Text>
                    </ModalHeader>
                    <ModalCloseButton />
                    
                    <ModalBody>
                        <VStack spacing={4} align="stretch">
                            <Text fontSize="sm" color="gray.600" textAlign="center">
                                We've sent a verification code to your mobile number:
                            </Text>
                            <Text fontSize="md" fontWeight="bold" color="#000B8C" textAlign="center">
                                {pendingRequest?.mobile}
                            </Text>
                            
                            <Box textAlign="center">
                                <Text fontSize="sm" color="gray.600" mb={3}>
                                    Enter the 4-digit OTP:
                                </Text>
                                <HStack justify="center">
                                    <PinInput 
                                        value={otp} 
                                        onChange={setOtp} 
                                        otp 
                                        placeholder="" 
                                        size="lg"
                                        focusBorderColor="#D6FF3F"
                                    >
                                        <PinInputField />
                                        <PinInputField />
                                        <PinInputField />
                                        <PinInputField />
                                    </PinInput>
                                </HStack>
                            </Box>

                            {timerActive && (
                                <Text fontSize="sm" color="gray.500" textAlign="center">
                                    OTP expires in: {formatTimer(otpTimer)}
                                </Text>
                            )}

                            {otpTimer === 0 && !timerActive && (
                                <Alert status="warning" size="sm">
                                    <AlertIcon />
                                    <Text fontSize="sm">OTP has expired. Please request a new one.</Text>
                                </Alert>
                            )}

                            {otpError && (
                                <Alert status="error" size="sm">
                                    <AlertIcon />
                                    <Text fontSize="sm">{otpError}</Text>
                                </Alert>
                            )}

                            <Flex justify="center" mt={4}>
                                <Button
                                    variant="link"
                                    size="sm"
                                    color="#000B8C"
                                    onClick={handleResendOtp}
                                    isLoading={otpSubmitting}
                                    loadingText="Sending..."
                                    isDisabled={otpSubmitting}
                                >
                                    Resend OTP
                                </Button>
                            </Flex>
                        </VStack>
                    </ModalBody>

                    <ModalFooter>
                        <Button 
                            variant="ghost" 
                            mr={3} 
                            onClick={handleModalClose}
                            isDisabled={otpSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button 
                            bg="#D6FF3F" 
                            color="#222"
                            _hover={{ bg: '#C0F000' }}
                            onClick={handleOtpSubmit}
                            isLoading={otpSubmitting}
                            loadingText="Verifying..."
                            isDisabled={otpSubmitting || !otp || otp.length !== 4 || otpTimer === 0}
                        >
                            Verify & Submit
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

        </Box>
    );
};

export default InternshipHero; 